/**
 * Catálogo fixo de badges (espelha netlify/functions/lib/teamBadges.js).
 * Badges custom vêm da API e têm `custom: true`.
 */

export const TEAM_BADGE_ICONS = [
  'crown',
  'gem',
  'shield',
  'gavel',
  'hand',
  'code',
  'cube',
  'palette',
  'megaphone',
  'sparkles',
  'headset',
  'handshake',
  'medal',
  'star',
  'bolt',
  'heart',
  'fire',
  'rocket',
];

export const TEAM_BADGE_TONES = [
  'gold',
  'amber',
  'violet',
  'blue',
  'emerald',
  'cyan',
  'orange',
  'pink',
  'rose',
  'indigo',
  'sky',
  'teal',
  'bronze',
];

export const TEAM_BADGE_CATALOG = [
  {
    id: 'founder',
    label: 'Fundador',
    description: 'Criou e moldou o Nexus desde o início',
    icon: 'crown',
    tone: 'gold',
  },
  {
    id: 'owner',
    label: 'Dono',
    description: 'Responsável máximo pelo projeto',
    icon: 'gem',
    tone: 'amber',
  },
  {
    id: 'admin',
    label: 'Admin',
    description: 'Administração geral do servidor',
    icon: 'shield',
    tone: 'violet',
  },
  {
    id: 'moderator',
    label: 'Moderador',
    description: 'Mantém a comunidade segura e justa',
    icon: 'gavel',
    tone: 'blue',
  },
  {
    id: 'helper',
    label: 'Helper',
    description: 'Ajuda jogadores no dia a dia',
    icon: 'hand',
    tone: 'emerald',
  },
  {
    id: 'developer',
    label: 'Dev',
    description: 'Constrói e mantém o ecossistema Nexus',
    icon: 'code',
    tone: 'cyan',
  },
  {
    id: 'builder',
    label: 'Builder',
    description: 'Constrói mapas, spawns e eventos',
    icon: 'cube',
    tone: 'orange',
  },
  {
    id: 'designer',
    label: 'Designer',
    description: 'Identidade visual e experiências',
    icon: 'palette',
    tone: 'pink',
  },
  {
    id: 'content',
    label: 'Conteúdo',
    description: 'Cria vídeos, posts e campanhas',
    icon: 'megaphone',
    tone: 'rose',
  },
  {
    id: 'events',
    label: 'Eventos',
    description: 'Organiza eventos e atividades',
    icon: 'sparkles',
    tone: 'indigo',
  },
  {
    id: 'support',
    label: 'Suporte',
    description: 'Atendimento e tickets',
    icon: 'headset',
    tone: 'sky',
  },
  {
    id: 'partner',
    label: 'Parceiro',
    description: 'Parceiro oficial do Nexus',
    icon: 'handshake',
    tone: 'teal',
  },
  {
    id: 'veteran',
    label: 'Veterano',
    description: 'Tempo de casa e dedicação',
    icon: 'medal',
    tone: 'bronze',
  },
  {
    id: 'staff',
    label: 'Staff',
    description: 'Membro da equipe Nexus',
    icon: 'star',
    tone: 'violet',
  },
];

export const TEAM_BADGE_BY_ID = Object.fromEntries(
  TEAM_BADGE_CATALOG.map((b) => [b.id, b])
);

export const TEAM_SOCIAL_KEYS = [
  { id: 'discord', label: 'Discord', placeholder: 'usuário ou invite' },
  { id: 'twitter', label: 'X / Twitter', placeholder: '@usuario' },
  { id: 'youtube', label: 'YouTube', placeholder: '@canal ou URL' },
  { id: 'twitch', label: 'Twitch', placeholder: 'canal' },
  { id: 'github', label: 'GitHub', placeholder: 'usuario' },
  { id: 'instagram', label: 'Instagram', placeholder: '@usuario' },
];

export function resolveTeamBadges(ids = [], catalog = TEAM_BADGE_CATALOG) {
  const byId = Object.fromEntries((catalog || []).map((b) => [b.id, b]));
  if (!Array.isArray(ids)) return [];
  const out = [];
  for (const item of ids) {
    const id = typeof item === 'string' ? item : item?.id;
    const badge = byId[String(id || '').trim()] || (typeof item === 'object' ? item : null);
    if (badge?.id && !out.some((b) => b.id === badge.id)) out.push(badge);
  }
  return out;
}

export function socialHref(key, value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const handle = raw.replace(/^@/, '');
  switch (key) {
    case 'twitter':
      return `https://twitter.com/${encodeURIComponent(handle)}`;
    case 'youtube':
      return `https://youtube.com/@${encodeURIComponent(handle)}`;
    case 'twitch':
      return `https://twitch.tv/${encodeURIComponent(handle)}`;
    case 'github':
      return `https://github.com/${encodeURIComponent(handle)}`;
    case 'instagram':
      return `https://instagram.com/${encodeURIComponent(handle)}`;
    case 'discord':
      if (/^\d{5,32}$/.test(handle)) return null;
      if (handle.includes('discord.gg') || handle.includes('discord.com')) {
        return raw.startsWith('http') ? raw : `https://${handle}`;
      }
      return null;
    default:
      return raw;
  }
}

/**
 * Formata lastSeen relativo em pt-BR.
 * Ex.: "Online agora", "há 5 minutos", "há 1 hora", "há 2 dias"
 */
export function formatLastSeenLabel(lastSeen, { online = false } = {}) {
  if (online) return 'Online agora';
  if (!lastSeen) return null;

  const ts = new Date(lastSeen).getTime();
  if (Number.isNaN(ts)) return null;

  const diffMs = Math.max(0, Date.now() - ts);
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Última vez online há pouco';
  if (mins < 60) {
    return mins === 1 ? 'Última vez online há 1 minuto' : `Última vez online há ${mins} minutos`;
  }

  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return hours === 1 ? 'Última vez online há 1 hora' : `Última vez online há ${hours} horas`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return days === 1 ? 'Última vez online há 1 dia' : `Última vez online há ${days} dias`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return months === 1 ? 'Última vez online há 1 mês' : `Última vez online há ${months} meses`;
  }

  const years = Math.floor(days / 365);
  return years === 1 ? 'Última vez online há 1 ano' : `Última vez online há ${years} anos`;
}
